/**
 * Free gift with purchase — auto add / auto remove.
 *
 * Config comes from the <gift-with-purchase data-config="..."> JSON blob
 * rendered by cart-gift.liquid: an array of rules (each pre-resolved to a gift
 * variant id + availability by Liquid), evaluated in the SAME order as the
 * discount function + checkout extension:
 *   - "threshold"  rule matches once the non-gift cart subtotal reaches
 *     rule.threshold
 *   - "collection" rule matches once the cart contains a non-gift item from
 *     rule.collection_handle (resolved via /collections/{handle}/products.json,
 *     mirroring cart-b1g1.js)
 *   - "discount_code" rule matches once the customer has applied
 *     rule.discount_code to the cart (a promo code that unlocks a gift), read
 *     from the cart's own discount_codes / cart_level_discount_applications
 *
 * On every cart update this element re-evaluates ALL rules and gives EACH
 * currently-matching rule its OWN gift line (line properties `_gift=true` +
 * `_gift_rule=<rule id>`, the latter identifying which rule the line belongs
 * to) — one gift per matching rule, not one gift per order. So a cart that
 * satisfies both a threshold rule and a collection rule gets both gifts.
 * Adding more qualifying items for the SAME rule (e.g. two items from a
 * collection rule's collection) still only earns that rule's single gift line.
 *
 * Each rule's gift is independently:
 *   - auto-added once that rule matches (and its variant is in stock), or
 *   - auto-removed once that rule no longer matches, or
 *   - auto-removed if its 100% discount has been knocked out by a
 *     non-combinable discount code (so the customer is never charged for it),
 *     re-offering it once that conflicting code is removed/changed.
 *
 * GRACE PERIOD: right after any gift-line mutation (add/remove/qty-fix),
 * Shopify's server-side discount recalculation can lag a moment behind the
 * cart response — a gift line can briefly read as "charged" (final price > 0)
 * before its 100% off is reapplied. Treating that as a real conflict would
 * remove the gift, which re-adds it on the next pass, which reads charged
 * again, forever (an infinite remove/add loop — this actually happened live).
 * So a "charged with no identifiable competing discount" observation is only
 * acted on once GRACE_PERIOD_MS has passed since the last mutation; within the
 * grace window we just wait and re-check once it elapses. A charge WITH an
 * identifiable competing discount code (discountSig truthy) is a real conflict
 * and is still acted on immediately, no grace period.
 *
 * The 100% discount on each gift line is applied server-side by the gift
 * discount function; this file only manages cart membership. Only ONE action
 * (one add/remove/qty-fix) is taken per reconcile() pass — the resulting cart
 * update event re-triggers reconcile(), so with N rules needing changes it
 * just takes N passes to settle, same single-step-at-a-time design as before.
 */
(function () {
  // Module-level lock so only one add/remove runs at a time across re-renders.
  let giftProcessing = false;
  // Set when an evaluation is requested while another is in flight, so the
  // request is retried once the current one finishes instead of being dropped.
  let giftReevaluatePending = false;

  // How long to wait after a gift-line mutation before trusting a "charged,
  // no identifiable competing discount" reading as a genuine, stuck state
  // rather than the server still catching up.
  const GRACE_PERIOD_MS = 3000;

  const GIFT_PROPERTY = "_gift";
  const GIFT_RULE_PROPERTY = "_gift_rule";

  // Cache each collection's product-id set per page load, keyed by handle.
  const productIdCache = new Map();

  async function fetchCollectionProductIds(handle) {
    if (productIdCache.has(handle)) return productIdCache.get(handle);

    const ids = new Set();
    try {
      for (let page = 1; page <= 40; page++) {
        const res = await fetch(
          `${window.Shopify?.routes?.root || "/"}collections/${handle}/products.json?limit=250&page=${page}`,
        );
        if (!res.ok) break;
        const data = await res.json();
        const products = data.products || [];
        for (const p of products) ids.add(p.id);
        if (products.length < 250) break;
      }
    } catch (e) {
      /* network error → empty set; this rule just won't match this pass */
    }

    productIdCache.set(handle, ids);
    return ids;
  }

  // Is `code` currently applied to this cart? Case/whitespace-insensitive.
  //
  // `discount_codes` lists what the customer has entered along with whether
  // Shopify considers it applicable — a code that doesn't qualify (its own
  // minimum spend unmet, expired, wrong market) must NOT unlock the gift, so
  // `applicable: false` is treated as not applied. Order-level applications
  // are checked too, since they carry the code as their title and are what
  // actually proves the code is live on this cart.
  function cartHasDiscountCode(cart, code) {
    const wanted = String(code || "").trim().toUpperCase();
    if (!wanted) return false;

    const entered = (cart.discount_codes || []).some(
      (entry) =>
        entry &&
        entry.applicable !== false &&
        String(entry.code || "").trim().toUpperCase() === wanted,
    );
    if (entered) return true;

    return (cart.cart_level_discount_applications || []).some(
      (application) =>
        application &&
        application.type === "discount_code" &&
        String(application.title || "").trim().toUpperCase() === wanted,
    );
  }

  class GiftWithPurchase extends HTMLElement {
    connectedCallback() {
      try {
        this.rules = JSON.parse(this.dataset.config || "{}").rules || [];
      } catch (e) {
        this.rules = [];
      }

      // Timestamp of the last successful add/remove/qty-fix mutation this
      // element performed — used by the charged-line grace period (see
      // header comment). Starts at 0 (never mutated) so an already-charged
      // gift line present on initial page load isn't held in an artificial
      // grace window.
      this.lastMutationAt = 0;
      this._recheckTimer = null;

      // Products that exist only as a gift (tagged "gift-only", listed by
      // cart-gift.liquid from every rule): a line of one without the gift
      // property was added by hand and is taken out (see reconcile()).
      this.giftOnlyIds = new Set(
        String(this.dataset.giftOnly || "")
          .split(",")
          .map((id) => Number(id))
          .filter(Boolean),
      );

      if (this.rules.length === 0 && this.giftOnlyIds.size === 0) {
        // No rule is active (none configured, all switched off, or all
        // outside their dates), but a gift line is still in the cart: one
        // pass takes it out, since nothing can match it any more.
        if (this.dataset.inCart === "true") {
          this.log("no active rules — removing leftover gift line(s)");
          this.evaluate();
          return;
        }
        this.log("not configured — no rules in data-config");
        return;
      }

      // Evaluate on connect (covers full page loads & section re-renders).
      this.evaluate();

      // Re-evaluate whenever any cart change is published.
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.evaluate();
      });
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      if (this._recheckTimer) {
        window.clearTimeout(this._recheckTimer);
        this._recheckTimer = null;
      }
    }

    // Schedules a single re-evaluate() after `ms`, used to re-check a gift
    // line that currently reads as charged but is still within its grace
    // period (see header comment). Coalesces to one pending timer.
    scheduleRecheck(ms) {
      if (this._recheckTimer) return;
      this._recheckTimer = window.setTimeout(() => {
        this._recheckTimer = null;
        this.evaluate();
      }, ms);
    }

    async evaluate() {
      // Serialize evaluations behind a single lock. If one is already running,
      // flag that another pass is needed and let the active run pick it up when
      // it finishes. The drain loop below then keeps reconciling until no new
      // request arrived, so the final cart state is always settled (every
      // rule's gift added or removed as appropriate) no matter how many cart
      // events fire while we're mid-flight — including when this element is
      // destroyed and recreated during a cart re-render (which happens on every
      // quantity change on the cart page).
      if (giftProcessing) {
        giftReevaluatePending = true;
        return;
      }
      giftProcessing = true;
      try {
        do {
          giftReevaluatePending = false;
          await this.reconcile();
        } while (giftReevaluatePending);
      } finally {
        giftProcessing = false;
      }
    }

    // Returns the set of configured rules whose condition is currently met, in
    // the same array order as the discount function + checkout extension.
    async matchingRules(cart) {
      const items = cart.items || [];
      // Shared with the reward stepper (reward-subtotal.js) so the bar and the
      // gift can never disagree about whether a threshold is met. Falls back to
      // the inline computation if that asset failed to load — a missing file
      // should degrade the bar, not break gift add/remove.
      const subtotal =
        window.FBReward && window.FBReward.subtotalMajor
          ? window.FBReward.subtotalMajor(cart)
          : items.reduce((sum, item) => {
              if (this.isGift(item)) return sum;
              return sum + (item.final_line_price || 0);
            }, 0) / 100;

      const hasNonGiftItem = items.some((item) => !this.isGift(item));

      const matched = [];
      for (const rule of this.rules) {
        if (!rule.variant_id) continue;

        if (rule.type === "threshold") {
          if (rule.threshold > 0 && subtotal >= rule.threshold) matched.push(rule);
        } else if (rule.type === "discount_code" && rule.discount_code) {
          if (hasNonGiftItem && cartHasDiscountCode(cart, rule.discount_code)) {
            matched.push(rule);
          }
        } else if (rule.type === "collection" && rule.collection_handle) {
          const productIds = await fetchCollectionProductIds(rule.collection_handle);
          const hasMatch = items.some(
            (item) => !this.isGift(item) && productIds.has(item.product_id),
          );
          if (hasMatch) matched.push(rule);
        }
      }
      return matched;
    }

    async reconcile() {
      let cart;
      try {
        cart = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
      } catch (e) {
        return;
      }

      const items = cart.items || [];
      const giftItems = items.filter((item) => this.isGift(item));
      const matched = await this.matchingRules(cart);
      const matchedRuleIds = new Set(matched.map((rule) => rule.id));

      // Pure DOM rendering (no cart mutation), safe to run every pass.
      this.renderUnlockMessages(matchedRuleIds);

      // Signature of every non-gift discount currently applied to the cart
      // (order-level codes/automatics + non-gift line discounts). When a
      // non-combinable discount knocks a gift's 100% off out, we remember this
      // signature so gifts aren't immediately re-added (and re-charged). The
      // moment the customer removes/changes that discount the signature changes
      // and gifts are offered again automatically.
      const discountSig = this.discountSignature(cart);
      const blockedSig = this.getBlockedSignature(cart.token);

      // A gift-only product added by hand (no gift property) can't be bought:
      // take it out first. The real gift line is added below as usual.
      for (const item of items) {
        if (!this.isGift(item) && this.giftOnlyIds.has(item.product_id)) {
          this.log("removing — gift-only product added by hand", {
            product: item.product_id,
          });
          await this.removeGift(item.key);
          return;
        }
      }

      // One action per pass: charged gift lines take priority (never let a
      // "free" gift keep being charged), then rule-mismatch removals, then
      // quantity self-heal, then adding a newly-matched rule's gift. Whichever
      // fires returns immediately; the cart-update event this triggers brings
      // us back here to handle the next one, until everything settles.
      for (const item of giftItems) {
        if ((item.final_line_price || 0) > 0) {
          if (discountSig) {
            // A real, identifiable competing discount — act immediately, no
            // grace period, and remember it so we don't re-add into it.
            this.log("removing — gift line is being charged (discount conflict)", {
              discountSig,
              rule: this.giftRuleId(item),
            });
            this.setBlockedSignature(cart.token, discountSig);
            await this.removeGift(item.key);
            return;
          }

          // No identifiable competing discount — this is very likely a
          // transient state right after a mutation (the server hasn't
          // recalculated the gift's 100% off yet). Give it GRACE_PERIOD_MS
          // before treating it as genuinely stuck; acting on it immediately
          // would remove+re-add forever since re-adding is itself a mutation
          // that can read as momentarily charged again.
          const elapsed = Date.now() - (this.lastMutationAt || 0);
          if (elapsed < GRACE_PERIOD_MS) {
            this.log("charge looks transient — within grace period, waiting", {
              elapsedMs: elapsed,
              rule: this.giftRuleId(item),
            });
            this.scheduleRecheck(GRACE_PERIOD_MS - elapsed);
            continue;
          }

          this.log(
            "removing — gift line still charged past grace period, no competing discount identified"
          );
          this.clearBlockedSignature(cart.token);
          await this.removeGift(item.key);
          return;
        }
      }

      for (const item of giftItems) {
        const ruleId = this.giftRuleId(item);
        if (ruleId && !matchedRuleIds.has(ruleId)) {
          this.log("removing — rule no longer matches", { rule: ruleId });
          await this.removeGift(item.key);
          return;
        }
      }

      for (const item of giftItems) {
        if (item.quantity > 1) {
          // Self-heal: a gift line must never exceed a single unit.
          this.log("fixing quantity — gift line had qty > 1", {
            rule: this.giftRuleId(item),
          });
          await this.setGiftQty(item.key, 1);
          return;
        }
      }

      // Gifts present and genuinely free — discounts combine fine.
      if (giftItems.length > 0) this.clearBlockedSignature(cart.token);

      // Add the gift for the first matched rule that doesn't already have one
      // in the cart, unless the currently applied discounts are the same set
      // that previously suppressed gifts. A falsy blockedSig (never blocked, or
      // blocked with no identifiable discount) never counts as a block.
      const presentRuleIds = new Set(
        giftItems.map((item) => this.giftRuleId(item)).filter(Boolean),
      );
      const ruleToAdd = matched.find((rule) => !presentRuleIds.has(rule.id));
      if (!ruleToAdd) return;

      if (!ruleToAdd.available) {
        this.log("suppressed — gift variant is out of stock", { rule: ruleToAdd.id });
        return;
      }

      if (!blockedSig || discountSig !== blockedSig) {
        await this.addGift(ruleToAdd.variant_id, ruleToAdd.id);
      } else {
        this.log("suppressed — blocked by a previous discount conflict", {
          discountSig,
          blockedSig,
          rule: ruleToAdd.id,
        });
      }
    }

    // Renders one visible "unlock" message bar per rule opted into
    // show_unlock_message, matching the look of cart-free-shipping-bar
    // (text + a 0%/100% progress fill — binary since these rules don't have a
    // continuous progress metric like the shipping bar's remaining-amount).
    // Text swaps between unlock_message_before / unlock_message_after
    // depending on whether the rule currently matches. Pure DOM update, no
    // cart mutation, so it's safe to call on every reconcile() pass.
    renderUnlockMessages(matchedRuleIds) {
      const messageRules = this.rules.filter((rule) => rule.show_unlock_message);

      if (messageRules.length === 0) {
        if (this._unlockMessagesContainer) {
          this._unlockMessagesContainer.remove();
          this._unlockMessagesContainer = null;
        }
        return;
      }

      if (!this._unlockMessagesContainer) {
        this._unlockMessagesContainer = document.createElement("div");
        this._unlockMessagesContainer.className = "wt-gift-unlock-messages";
        this.appendChild(this._unlockMessagesContainer);
      }
      const container = this._unlockMessagesContainer;

      for (const rule of messageRules) {
        const matched = matchedRuleIds.has(rule.id);
        const text = matched ? rule.unlock_message_after : rule.unlock_message_before;
        if (!text) continue;

        let bar = container.querySelector(`[data-gift-rule-id="${CSS.escape(rule.id)}"]`);
        if (!bar) {
          bar = document.createElement("div");
          bar.className = "wt-gift-unlock-bar";
          bar.dataset.giftRuleId = rule.id;
          bar.innerHTML =
            '<p class="wt-gift-unlock-bar__text"></p>' +
            '<div class="wt-progress-bar"><div class="wt-progress-bar__fill"></div></div>';
          container.appendChild(bar);
        }
        bar.querySelector(".wt-gift-unlock-bar__text").textContent = text;
        bar.querySelector(".wt-progress-bar__fill").style.width = matched ? "100%" : "0%";
      }

      // Drop bars for rules that no longer opt into the message (config only
      // changes via a fresh page/section render anyway, but keep it tidy).
      const currentIds = new Set(messageRules.map((rule) => rule.id));
      container.querySelectorAll("[data-gift-rule-id]").forEach((bar) => {
        if (!currentIds.has(bar.dataset.giftRuleId)) bar.remove();
      });
    }

    log(message, extra) {
      // Prefixed + visible by default so it's easy to spot in devtools while
      // debugging a "gift didn't add" report, without being alarming noise.
      console.debug(`[gift-with-purchase] ${message}`, extra || "");
    }

    isGift(item) {
      return !!(item.properties && item.properties[GIFT_PROPERTY] === "true");
    }

    giftRuleId(item) {
      return (item.properties && item.properties[GIFT_RULE_PROPERTY]) || null;
    }

    /**
     * Stable fingerprint of all non-gift discounts on the cart (order-level
     * applications + line-level allocations on non-gift lines). Changes whenever
     * a discount code or automatic promotion is added, removed or swapped.
     */
    discountSignature(cart) {
      const titles = [];
      (cart.cart_level_discount_applications || []).forEach((a) => {
        if (a && a.title) titles.push(`c:${a.title}`);
      });
      (cart.items || []).forEach((item) => {
        if (this.isGift(item)) return;
        (item.line_level_discount_allocations || []).forEach((al) => {
          const da = al && al.discount_application;
          if (da && da.title) titles.push(`l:${da.title}`);
        });
      });
      return titles.sort().join("|");
    }

    blockKey(token) {
      return `gwp_block_${token || "cart"}`;
    }

    getBlockedSignature(token) {
      try {
        return sessionStorage.getItem(this.blockKey(token));
      } catch (e) {
        return null;
      }
    }

    setBlockedSignature(token, sig) {
      try {
        sessionStorage.setItem(this.blockKey(token), sig);
      } catch (e) {
        /* sessionStorage unavailable — ignore */
      }
    }

    clearBlockedSignature(token) {
      try {
        sessionStorage.removeItem(this.blockKey(token));
      } catch (e) {
        /* sessionStorage unavailable — ignore */
      }
    }

    async addGift(variantId, ruleId) {
      const res = await fetch(`${routes.cart_add_url}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: {
                [GIFT_PROPERTY]: "true",
                [GIFT_RULE_PROPERTY]: ruleId || "",
              },
            },
          ],
        }),
      }).catch(() => null);
      if (res && res.ok) {
        this.lastMutationAt = Date.now();
        this.notifyCartChanged();
      }
    }

    async removeGift(key) {
      const res = await fetch(`${routes.cart_change_url}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity: 0 }),
      }).catch(() => null);
      if (res && res.ok) {
        this.lastMutationAt = Date.now();
        this.notifyCartChanged();
      }
    }

    async setGiftQty(key, quantity) {
      const res = await fetch(`${routes.cart_change_url}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity }),
      }).catch(() => null);
      if (res && res.ok) {
        this.lastMutationAt = Date.now();
        this.notifyCartChanged();
      }
    }

    notifyCartChanged() {
      // Refresh the drawer + cart bubble without opening the drawer.
      document.dispatchEvent(new CustomEvent("cart-drawer:refresh"));
      // Refresh the cart page line items / other cartUpdate subscribers.
      publish(PUB_SUB_EVENTS.cartUpdate, { source: "gift-with-purchase" });
    }
  }

  customElements.define("gift-with-purchase", GiftWithPurchase);
})();

